import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Form,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiPackage,
  FiTruck,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye,
  FiShoppingBag,
  FiStar,
  FiCornerDownLeft,
  FiRefreshCw,
} from "react-icons/fi";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import Button from "../../components/general/button/Button";
import Pagination from "../../components/general/pagination/Pagination";
import { OrdersPageSkeleton } from "../../components/general/skeleton/SkeletonBlock";
import "../../assets/css/customer/customerDashboard/orders/Orders.css";
import ActionDropdown from "../../components/customer/dropdown/ActionDropdown";
import axios from "axios";
import Swal from "sweetalert2";
import MyContext from "../../context/MyContext";

const Orders = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, setSelectedOrder, authToken } = useContext(MyContext);

  const [loading, setLoading] = useState(false);
  const [filterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isViewingReview, setIsViewingReview] = useState(false);
  const [selectedReviewOrder, setSelectedReviewOrder] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [kmcustomerId, setKmcustomerId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [activeTab, setActiveTab] = useState("all");

  const [ordersList, setOrdersList] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [completedOrders, setCompletedOrders] = useState(0);
  const [cancelledOrders, setCancelledOrders] = useState(0);

  useEffect(() => {
    const loggedInCustomer = JSON.parse(
      localStorage.getItem("loggedInCustomer"),
    );
    if (loggedInCustomer) {
      setKmcustomerId(loggedInCustomer.id);
      setCustomerName(loggedInCustomer.name);
      setCustomerEmail(loggedInCustomer.email);
      viewAllOrders();
    }
  }, []);

  const viewAllOrders = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/order/viewAllOrders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setOrdersList(response.data.orders);
          setTotalOrders(response.data.totalOrders);
          setPendingOrders(response.data.pendingOrders);
          setCompletedOrders(response.data.completedOrders);
          setCancelledOrders(response.data.cancelledOrders);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load orders.",
        });
      });
  };

  const submitReview = () => {
    if (!rating) {
      Swal.fire({ title: "Error!", text: "Please select a rating", icon: "error", confirmButtonColor: "#097d43" });
      return;
    }

    const formData = new FormData();
    formData.append("kmseller_id", selectedReviewOrder?.kmseller_id);
    formData.append("kmorder_id", selectedReviewOrder?.id);
    formData.append("rating", rating);
    formData.append("review", reviewText);

    axios
      .post(`${baseUrl}/customer/review/submitReview`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({ title: "Review Submitted!", icon: "success", confirmButtonColor: "#097d43" });
          setShowReviewModal(false);
          const loggedInCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));
          if (loggedInCustomer) viewAllOrders();
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
        }
      })
      .catch((error) => {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to submit review." });
      });
  };

  const stats = [
    {
      icon: FiPackage,
      title: "Total Orders",
      value: totalOrders.toString(),
      color: "#097d43",
      bgColor: "#e8f5f0",
    },
    {
      icon: FiClock,
      title: "Pending",
      value: pendingOrders.toString(),
      color: "#ffc107",
      bgColor: "#fff8e1",
    },
    {
      icon: FiTruck,
      title: "Completed",
      value: completedOrders.toString(),
      color: "#28a745",
      bgColor: "#e8f5e9",
    },
    {
      icon: FiXCircle,
      title: "Cancelled",
      value: cancelledOrders.toString(),
      color: "#dc3545",
      bgColor: "#ffebee",
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    const map = {
      completed: { icon: FiCheckCircle, text: "Completed", color: "#28a745", bgColor: "#e8f5e9" },
      delivered: { icon: FiCheckCircle, text: "Delivered", color: "#28a745", bgColor: "#e8f5e9" },
      pending: { icon: FiClock, text: "Pending", color: "#b8860b", bgColor: "#fff8e1" },
      awaiting_bids: { icon: FiClock, text: "Awaiting Bids", color: "#b8860b", bgColor: "#fff8e1" },
      awaiting_payment: { icon: FiClock, text: "Awaiting Payment", color: "#b8860b", bgColor: "#fff8e1" },
      open: { icon: FiRefreshCw, text: "Finding New Driver", color: "#e65100", bgColor: "#fff3e0" },
      awaiting_reselection: { icon: FiRefreshCw, text: "Select New Driver", color: "#e65100", bgColor: "#fff3e0" },
      processing: { icon: FiPackage, text: "Processing", color: "#097d43", bgColor: "#e8f5f0" },
      shipping: { icon: FiTruck, text: "Shipping", color: "#0d6efd", bgColor: "#e7f1ff" },
      cancelled: { icon: FiXCircle, text: "Cancelled", color: "#dc3545", bgColor: "#ffebee" },
      returned: { icon: FiCornerDownLeft, text: "Returned", color: "#6f42c1", bgColor: "#f2ecfc" },
    };
    const cfg = map[s] || map.pending;
    const Icon = cfg.icon;
    return (
      <span
        className="status-badge"
        style={{
          color: cfg.color,
          backgroundColor: cfg.bgColor,
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 12px",
          borderRadius: "50px",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        <Icon size={12} className="me-1" />
        {cfg.text}
      </span>
    );
  };

  const getRefundBadge = (paymentStatus) => {
    if (paymentStatus === "Refund Pending") {
      return (
        <span
          className="status-badge"
          style={{
            color: "#e65100",
            backgroundColor: "#fff3e0",
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 10px",
            borderRadius: "50px",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          Refund Pending
        </span>
      );
    }
    if (paymentStatus === "Refunded") {
      return (
        <span
          className="status-badge"
          style={{
            color: "#097d43",
            backgroundColor: "#e8f5f0",
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 10px",
            borderRadius: "50px",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          Refunded
        </span>
      );
    }
    return <span style={{ color: "#999", fontSize: 12 }}>—</span>;
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const renderSellerCell = (seller) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 160,
      }}
    >
      {seller?.image ? (
        <img
          src={`${baseUrlForFiles}/${seller.image}`}
          alt={seller?.name}
          style={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: "50%",
            objectFit: "cover",
            border: "1px solid #dee2e6",
          }}
        />
      ) : (
        <div
          style={{
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #097d43 0%, #0ac46c 100%)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {getInitials(seller?.name)}
        </div>
      )}
      <div>
        <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, color: "#333", whiteSpace: "nowrap" }}>
          {seller?.name || "N/A"}
        </p>
        <p style={{ margin: 0, fontSize: "0.75rem", color: "#6c757d", whiteSpace: "nowrap" }}>
          {seller?.email}
        </p>
      </div>
    </div>
  );

  // ── Actions ───────────────────────────────────────────────────
  const getActions = (order) => {
    const s = order.status?.toLowerCase();
    const base = [
      {
        label: "View Details",
        icon: <FiEye size={15} />,
        variant: "view",
        onClick: () => {
          setSelectedOrder(order);
          navigate("/customer/order-details");
        },
      },
    ];

    // ── NAYA: pehli dafa bidding (order abhi place hua, driver wait ho
    // raha hai) — "View Bids" ── aur driver drop-out reselection —
    // "Reassign Driver" — dono isi generic CustomerOrderBids page pe
    // navigate karte hain, sirf label/icon farak hai ──
    if (s === "awaiting_bids") {
      base.push({
        label: "View Bids",
        icon: <FiTruck size={15} />,
        variant: "success",
        onClick: () => {
          navigate("/customer/order-bids", { state: { kmorder_id: order.id } });
        },
      });
    } else if (s === "open" || s === "awaiting_reselection") {
      base.push({
        label: "Reassign Driver",
        icon: <FiRefreshCw size={15} />,
        variant: "success",
        onClick: () => {
          navigate("/customer/order-bids", { state: { kmorder_id: order.id } });
        },
      });
    }

    if (s === "delivered") {
      if (order.rating && order.review) {
        base.push({
          label: "View Review",
          icon: <FiStar size={15} />,
          onClick: () => {
            setSelectedReviewOrder(order);
            setRating(order.rating);
            setReviewText(order.review);
            setIsViewingReview(true);
            setShowReviewModal(true);
          },
        });
      } else {
        base.push({
          label: "Write a Review",
          icon: <FiStar size={15} />,
          onClick: () => {
            setSelectedReviewOrder(order);
            setRating(0);
            setReviewText("");
            setIsViewingReview(false);
            setShowReviewModal(true);
          },
        });
      }
    }
    return base;
  };

  const cancelledStatusOrders = ordersList.filter(
    (o) => o.status?.toLowerCase() === "cancelled"
  );
  const returnedStatusOrders = ordersList.filter(
    (o) => o.status?.toLowerCase() === "returned"
  );
  const reassignDriverOrders = ordersList.filter(
    (o) =>
      o.paymentStatus === "Paid" &&
      ["open", "awaiting_reselection"].includes(o.status?.toLowerCase())
  );

  const displayedOrders =
    activeTab === "cancelled" ? cancelledStatusOrders :
    activeTab === "returned"  ? returnedStatusOrders :
    activeTab === "reassign"  ? reassignDriverOrders :
    ordersList;

  const searchedOrders = displayedOrders.filter((order) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const idMatch = order.id?.toString().includes(q);
    const productMatch = order.kmcarts?.some((c) =>
      c.kmproduct?.name?.toLowerCase().includes(q)
    );
    return idMatch || productMatch;
  });

  if (loading)
    return (
      <CustomerMasterLayout>
        <OrdersPageSkeleton />
      </CustomerMasterLayout>
    );

  return (
    <CustomerMasterLayout>
      <Container fluid className="orders-page">
        <div className="orders-header">
          <div className="orders-header-left">
            <div className="orders-icon-wrapper">
              <FiShoppingBag size={28} />
            </div>
            <div>
              <h2 className="orders-page-title">My Orders</h2>
              <p className="orders-subtitle">{totalOrders} orders in total</p>
            </div>
          </div>
        </div>

        <Row className="g-3 mb-4">
          {stats.map((s, i) => (
            <Col key={i} xs={12} sm={6} xl={3}>
              <Card className="stats-card">
                <Card.Body>
                  <div className="stats-card-content">
                    <div>
                      <p className="stats-label">{s.title}</p>
                      <h3 className="stats-value">{s.value}</h3>
                    </div>
                    <div
                      className="stats-icon-wrapper"
                      style={{ backgroundColor: s.bgColor }}
                    >
                      <s.icon size={26} style={{ color: s.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="orders-page-tabs" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button
            className={`orders-page-tab ${activeTab === "all" ? "orders-page-tab--active" : ""}`}
            onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
          >
            All Orders <span className="orders-page-tab__count">{ordersList.length}</span>
          </button>
          <button
            className={`orders-page-tab ${activeTab === "reassign" ? "orders-page-tab--active" : ""}`}
            onClick={() => { setActiveTab("reassign"); setCurrentPage(1); }}
          >
            <FiRefreshCw size={14} style={{ marginRight: 4 }} /> Reassign Driver
            {reassignDriverOrders.length > 0 && (
              <span className="orders-page-tab__count">{reassignDriverOrders.length}</span>
            )}
          </button>
          <button
            className={`orders-page-tab ${activeTab === "cancelled" ? "orders-page-tab--active" : ""}`}
            onClick={() => { setActiveTab("cancelled"); setCurrentPage(1); }}
          >
            <FiXCircle size={14} style={{ marginRight: 4 }} /> Cancelled
            {cancelledStatusOrders.length > 0 && (
              <span className="orders-page-tab__count">{cancelledStatusOrders.length}</span>
            )}
          </button>
          <button
            className={`orders-page-tab ${activeTab === "returned" ? "orders-page-tab--active" : ""}`}
            onClick={() => { setActiveTab("returned"); setCurrentPage(1); }}
          >
            <FiCornerDownLeft size={14} style={{ marginRight: 4 }} /> Returned
            {returnedStatusOrders.length > 0 && (
              <span className="orders-page-tab__count">{returnedStatusOrders.length}</span>
            )}
          </button>
        </div>

        {activeTab === "reassign" && reassignDriverOrders.length > 0 && (
          <Card className="mb-3" style={{ borderLeft: "4px solid #e65100" }}>
            <Card.Body style={{ padding: "14px 20px" }}>
              <p style={{ margin: 0, fontSize: 14, color: "#333" }}>
                <strong>Your delivery driver became unavailable.</strong>{" "}
                Open the order to select a new driver — no additional payment is needed.
              </p>
            </Card.Body>
          </Card>
        )}

        {activeTab === "all" && (
          <Card className="filters-card mb-4">
            <Card.Body>
              <Row className="g-3 align-items-center">
                <Col xs={12} md={6}>
                  <InputGroup>
                    <InputGroup.Text className="search-icon">
                      <FiSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search by order ID or product name..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      className="search-input"
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <Card className="orders-table-card">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Seller</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    {(activeTab === "cancelled" || activeTab === "returned") && <th>Refund</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchedOrders
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((order, i) => (

                      <tr key={order.id}>
                        <td><span className="order-id">{order.id}</span></td>
                        <td><span className="order-date">{formatDate(order.created_at)}</span></td>
                        <td>{renderSellerCell(order.kmseller)}</td>
                        <td><span className="items-count">{order.kmcarts?.length || 0} items</span></td>
                        <td>
                          <span className="order-total">
                            Rs. {order.totalPrice?.toLocaleString()}
                          </span>
                        </td>
                        <td>{getStatusBadge(order.status)}</td>
                        {(activeTab === "cancelled" || activeTab === "returned") && (
                          <td>{getRefundBadge(order.paymentStatus)}</td>
                        )}
                        <td>
                          {(() => {
                            const actions = getActions(order);
                            if (actions.length === 1) {
                              const only = actions[0];
                              return (
                                <button
                                  className="orders-single-action-btn"
                                  title={only.label}
                                  onClick={only.onClick}
                                  style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    border: "1px solid #dee2e6",
                                    background: "#fff",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    color: "#097d43",
                                  }}
                                >
                                  {only.icon}
                                </button>
                              );
                            }
                            return (
                              <ActionDropdown
                                isOpen={openDropdownId === order.id}
                                onToggle={() =>
                                  setOpenDropdownId(
                                    openDropdownId === order.id ? null : order.id,
                                  )
                                }
                                actions={actions}
                              />
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  {searchedOrders.length === 0 && (
                    <tr>
                      <td colSpan="10" className="text-center py-5 text-muted">
                        {searchQuery.trim() ? "No orders match your search" :
                         activeTab === "cancelled" ? "No cancelled orders" :
                         activeTab === "returned" ? "No returned orders" :
                         activeTab === "reassign" ? "No orders need a new driver right now" :
                         "No orders found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={searchedOrders.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(p) => {
                setCurrentPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onItemsPerPageChange={(v) => {
                setItemsPerPage(v);
                setCurrentPage(1);
              }}
            />
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        centered
        className="review-modal"
      >
        <Modal.Header closeButton className="review-modal-header">
          <Modal.Title className="review-modal-title">
            <FiStar className="me-2" /> {isViewingReview ? "Your Review" : "Write a Review"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="review-modal-body">
          {selectedReviewOrder && (
            <>
              <div className="order-info-review">
                <p className="mb-1">
                  <strong>Order ID:</strong> {selectedReviewOrder.id}
                </p>
                <p className="mb-1 text-muted small">
                  <strong>Seller:</strong>{" "}
                  {selectedReviewOrder.kmseller?.shop_name}
                </p>
                <p className="mb-0 text-muted small">
                  {selectedReviewOrder.kmcarts?.length} items — Rs.{" "}
                  {selectedReviewOrder.totalPrice?.toLocaleString()}
                </p>
              </div>
              <div className="rating-section mb-4 mt-3">
                <label className="form-label fw-semibold mb-2">
                  Your Rating *
                </label>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FiStar
                      key={star}
                      size={32}
                      className={`star-icon ${star <= (hoveredRating || rating) ? "filled" : ""} ${isViewingReview ? "readonly" : ""}`}
                      onClick={() => !isViewingReview && setRating(star)}
                      onMouseEnter={() => !isViewingReview && setHoveredRating(star)}
                      onMouseLeave={() => !isViewingReview && setHoveredRating(0)}
                    />
                  ))}
                </div>
                {rating > 0 && (
                  <p className="rating-text mt-2 mb-0">
                    You rated:{" "}
                    <strong>
                      {rating} star{rating > 1 ? "s" : ""}
                    </strong>
                  </p>
                )}
              </div>
              <div className="review-text-section">
                <label className="form-label fw-semibold mb-2">Your Review {isViewingReview ? "" : "(Optional)"}</label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Share your experience..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className="review-textarea"
                  readOnly={isViewingReview}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="review-modal-footer">
          {isViewingReview ? (
            <Button variant="light" onClick={() => setShowReviewModal(false)}>Close</Button>
          ) : (
            <>
              <Button variant="light" onClick={() => setShowReviewModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => submitReview()}>Submit Review</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </CustomerMasterLayout>
  );
};

export default Orders;
